import { useEffect } from "react";

/**
 * On-screen keyboard awareness for the mobile PWA (iOS keeps the layout
 * viewport and only shrinks the visual viewport when the keyboard opens).
 *
 * While the keyboard is up, `data-keyboard-open` is set on <html> so CSS can
 * hide the floating tab bar / "+" capsule and tighten sheet footers, and the
 * focused field is scrolled to the centre of its scroll container once the
 * viewport has settled, so it is never left under the keyboard.
 */

/** Minimum viewport loss (CSS px) that counts as a keyboard, not browser UI. */
export const KEYBOARD_MIN_HEIGHT = 150;

const NON_TEXT_INPUTS = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/** True for elements that raise the on-screen keyboard when focused. */
export const isEditableElement = (
  element: Element | null,
): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly;
  if (element instanceof HTMLInputElement) {
    return !element.readOnly && !NON_TEXT_INPUTS.has(element.type);
  }
  return false;
};

/** Height (CSS px) the visual viewport lost to the keyboard, 0 when none. */
export const keyboardHeight = (win: Window = window): number => {
  const viewport = win.visualViewport;
  if (!viewport) return 0;
  // Pinch-zoom shrinks `height` too: scale it back to layout pixels.
  return Math.max(0, win.innerHeight - viewport.height * viewport.scale);
};

export const isKeyboardOpen = (win: Window = window): boolean =>
  keyboardHeight(win) > KEYBOARD_MIN_HEIGHT &&
  isEditableElement(win.document.activeElement);

const prefersReducedMotion = (win: Window) =>
  win.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export const useVisualViewport = (enabled = true): void => {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!enabled || !viewport) return;
    const root = document.documentElement;
    let frame = 0;

    const revealFocused = () => {
      cancelAnimationFrame(frame);
      // Next frame: the sheet has re-laid out to the new viewport height.
      frame = requestAnimationFrame(() => {
        const element = document.activeElement;
        if (!isEditableElement(element)) return;
        element.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: prefersReducedMotion(window) ? "auto" : "smooth",
        });
      });
    };

    const sync = () => {
      const open = isKeyboardOpen(window);
      const wasOpen = root.dataset.keyboardOpen != null;
      if (open) {
        root.dataset.keyboardOpen = "";
        root.style.setProperty("--keyboard-height", `${keyboardHeight()}px`);
        revealFocused();
      } else if (wasOpen) {
        delete root.dataset.keyboardOpen;
        root.style.removeProperty("--keyboard-height");
      }
    };

    // Focus moves after `focusout`: re-check once it has landed.
    const syncSoon = () => window.setTimeout(sync, 0);

    viewport.addEventListener("resize", sync);
    document.addEventListener("focusin", sync);
    document.addEventListener("focusout", syncSoon);
    sync();

    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", sync);
      document.removeEventListener("focusin", sync);
      document.removeEventListener("focusout", syncSoon);
      delete root.dataset.keyboardOpen;
      root.style.removeProperty("--keyboard-height");
    };
  }, [enabled]);
};
